import React, { FC } from "react";
import classNames from "classnames";

import { Avatar } from "../../common";
import {
  PaymentRequest,
  PaymentRequestStatus,
} from "../../../types/PaymentRequestTypes";
import { formatDate } from "../../../utils/date";
import { CURRENCY_MAP } from "../../../constants";
import { formatAmount } from "../../../utils/numbers/money";

type Props = {
  data: PaymentRequest;
  onClick?: () => void;
};

type StatusConfig = {
  icon: string;
  color: string;
  text: string;
};

const ConfigByStatus: Record<PaymentRequestStatus, StatusConfig> = {
  [PaymentRequestStatus.PENDING]: {
    icon: "hourglass_empty",
    color: "text-purple-700",
    text: "pending",
  },
  [PaymentRequestStatus.PROCESSING]: {
    icon: "hourglass_bottom",
    color: "text-purple-700",
    text: "processing",
  },
  [PaymentRequestStatus.PENDING_REVIEW]: {
    icon: "hourglass_top",
    color: "text-purple-700",
    text: "pending review",
  },
  [PaymentRequestStatus.PAID]: {
    icon: "Payments",
    color: "text-green-500",
    text: "paid",
  },
  [PaymentRequestStatus.REJECTED]: {
    icon: "Close",
    color: "text-red-500",
    text: "rejected",
  },
};

export const PaymentRequestCard: FC<Props> = ({ data, onClick }) => {
  const {
    payer_name,
    status,
    currency,
    outgoing_currency,
    amount,
    created_at,
    paid_at,
    transaction_details: { outgoing_amount },
  } = data;

  const { icon, color, text: statusText } = ConfigByStatus[status];

  const cardDate = paid_at ? paid_at : created_at;

  return (
    <div
      className={classNames(
        "flex justify-between border border-skyblue-200 bg-skyblue-50 rounded-2xl px-4 py-2 gap-3",
        {
          "cursor-pointer": onClick,
        }
      )}
      onClick={onClick}
    >
      <div>
        <Avatar
          icon={icon}
          iconColor={color}
          title={payer_name}
          type="business"
        />
      </div>

      <div className="flex-1 flex flex-col gap-1">
        <span className="font-semibold">{payer_name}</span>

        <span className={classNames(color, "font-medium text-xs")}>
          Transfer {statusText}
        </span>

        <span className="font-medium text-violet-300 text-xs">
          {formatDate(cardDate.toString())}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-right font-semibold">
          {CURRENCY_MAP[currency].symbol} {formatAmount(amount)}
        </span>

        <span className="text-right font-medium text-xs text-violet-300">
          {CURRENCY_MAP[outgoing_currency].symbol}{" "}
          {formatAmount(outgoing_amount)}
        </span>
      </div>
    </div>
  );
};
